// Admin Payments & Billing v2 — /api/admin/v2/{payments,invoices,transactions,subscriptions,wallets,orders,payment-methods}

export type PaymentListStatus = 'pending' | 'completed' | 'canceled' | 'refunded';
export type InvoiceStatus =
  | 'draft'
  | 'pending'
  | 'paid'
  | 'overdue'
  | 'canceled'
  | 'voided'
  | 'refunded';
export type BillingType = 'one_time' | 'recurring' | 'plan_renewal' | 'order';
export type SubscriptionBillingStatus =
  | 'active'
  | 'paused'
  | 'past_due'
  | 'canceled'
  | 'expired'
  | 'trialing'
  | 'payment_pending';
export type IntervalType = 'weekly' | 'monthly' | 'quarterly' | 'semiannual' | 'yearly';
export type WalletDirection = 'credit' | 'debit' | 'hold' | 'release';
export type RefundRequestStatus = 'pending' | 'approved' | 'rejected' | 'processed';
export type RefundRequestAction = 'approve' | 'reject' | 'process';

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more?: boolean;
}

export interface PaymentSource {
  psid: number;
  name?: string;
  name_system?: string;
  channel?: string;
  [key: string]: unknown;
}

export interface PaymentMethodSummary {
  psid: number;
  channel?: string;
  name?: string;
  [key: string]: unknown;
}

export interface PaymentLinkInvoice {
  type: 'invoice';
  invid: number;
  ffid?: string;
}

export interface PaymentLinkEntity {
  type: 'entity';
  eid: number;
  uuid?: string;
  ffid?: string;
  name?: string;
  slug?: string;
  photo?: string | null;
}

export type PaymentLink = PaymentLinkInvoice | PaymentLinkEntity | { type: string; [key: string]: unknown };

export interface UserSummary {
  uid: number;
  preferred_name?: string | null;
  username?: string | null;
  name?: string | null;
  email?: string | null;
  ffid?: string | null;
}

export interface OrgSummary {
  oid: number;
  name?: string | null;
  ffid?: string | null;
}

export interface EntitySummary {
  eid: number;
  name?: string | null;
  slug?: string | null;
  ffid?: string | null;
  uuid?: string | null;
}

export interface StoreSummary {
  storeid: number;
  name?: string | null;
  slug?: string | null;
  ffid?: string | null;
}

export interface Payment {
  pid: number;
  uuid?: string;
  ffid?: string;
  uid?: number | null;
  oid?: number | null;
  invid?: number | null;
  psid?: number | null;
  vid?: number | null;
  bpmid?: number | null;
  amount?: number;
  amount_paid?: number;
  ccode?: string;
  /** API may return a normalized status string in addition to flag columns. */
  status?: PaymentListStatus | string | null;
  ref_id?: string | null;
  verified?: number | boolean | null;
  fulfilled?: number | boolean | null;
  canceled?: number | boolean | null;
  refund?: number | boolean | null;
  tester?: number | boolean | null;
  date_added?: string;
  date_modified?: string;
  ref_notes?: string | null;
  invoice?: unknown;
  invoice_ffid?: string | null;
  invoice_status?: InvoiceStatus | string | null;
  source?: PaymentSource | null;
  payment_method_summary?: PaymentMethodSummary | null;
  links?: PaymentLink[];
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
  entity_summary?: EntitySummary | null;
  [key: string]: unknown;
}

export interface Invoice {
  invid: number;
  uuid?: string;
  ffid?: string;
  uid?: number | null;
  oid?: number | null;
  plid?: number | null;
  status?: InvoiceStatus | string;
  billing_type?: BillingType | string;
  /** Display amount — normalized from amount_posttax when API omits `amount`. */
  amount?: number;
  amount_pretax?: number;
  amount_posttax?: number;
  amount_tax?: number;
  amount_discount?: number;
  amount_refund?: number;
  amount_due?: number;
  ccode?: string;
  paid?: number | boolean;
  canceled?: number | boolean;
  tester?: number | boolean;
  notes?: string | null;
  date_added?: string;
  date_paid?: string | null;
  date_due?: string | null;
  payments?: Payment[];
  links?: PaymentLink[];
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
  entity_summary?: EntitySummary | null;
  [key: string]: unknown;
}

export interface Subscription {
  plid: number;
  uuid?: string;
  ffid?: string;
  uid?: number | null;
  oid?: number | null;
  eid?: number | null;
  toid?: number | null;
  billing_status?: SubscriptionBillingStatus | string;
  interval_type?: IntervalType | string;
  amount?: number;
  ccode?: string;
  auto_charge?: number | boolean;
  cancel_at_period_end?: number | boolean;
  bpmid?: number | null;
  plan_name?: string | null;
  date_started?: string | null;
  date_expired?: string | null;
  canceled_at?: string | null;
  trial_end?: string | null;
  tester?: number | boolean;
  invoices?: Invoice[];
  recent_invoices?: Invoice[];
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
  entity_summary?: EntitySummary | null;
  [key: string]: unknown;
}

export interface LedgerTransaction {
  tid: number;
  uuid?: string;
  ttid?: number | null;
  ttype?: string | null;
  uid_from?: number | null;
  uid_to?: number | null;
  uid_beneficiary?: number | null;
  oid?: number | null;
  oid_from?: number | null;
  eid?: number | null;
  toid?: number | null;
  invid?: number | null;
  pid?: number | null;
  storeid?: number | null;
  amount?: number;
  amount_less?: number | null;
  tax?: number | null;
  quantity?: number | null;
  ccode?: string;
  fulfilled?: number | boolean;
  date_added?: string;
  invoice?: Invoice | null;
  payment?: Payment | null;
  entity?: unknown;
  tradeable_object?: unknown;
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
  entity_summary?: EntitySummary | null;
  store_summary?: StoreSummary | null;
  [key: string]: unknown;
}

export interface Wallet {
  wid: number;
  uid?: number | null;
  oid?: number | null;
  ccode?: string;
  balance?: number;
  hold?: number | boolean;
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
  [key: string]: unknown;
}

export interface WalletLookupResult {
  wallets: Wallet[];
  uid?: number | null;
  oid?: number | null;
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
  /** Present when API returns a single wallet object. */
  wid?: number;
}

export interface WalletTransaction {
  wtid: number;
  wid?: number;
  type?: string;
  amount?: number;
  balance_after?: number;
  reference_type?: string | null;
  reference_id?: string | number | null;
  description?: string | null;
  date_added?: string;
  [key: string]: unknown;
}

export interface Order {
  orderid: number;
  uuid?: string;
  ffid?: string;
  /** Normalized from `uid_buyer` when present. */
  uid?: number | null;
  uid_buyer?: number | null;
  storeid?: number | null;
  invid?: number | null;
  pid?: number | null;
  status?: string;
  /** Normalized from `amount_total` when present. */
  total?: number;
  amount_total?: number;
  /** Normalized from `currency_code` when present. */
  ccode?: string;
  currency_code?: string;
  tracking_number?: string | null;
  date_added?: string;
  date_paid?: string | null;
  user_summary?: UserSummary | null;
  store_summary?: StoreSummary | null;
  [key: string]: unknown;
}

export interface RefundRequest {
  rrid: number;
  orderid?: number;
  /** Normalized from `uid_buyer` when present. */
  uid?: number | null;
  uid_buyer?: number | null;
  storeid?: number | null;
  status?: RefundRequestStatus | string;
  reason?: string | null;
  amount_requested?: number;
  pid_refund?: number | null;
  /** Normalized from `seller_notes` when present. */
  notes?: string | null;
  seller_notes?: string | null;
  /** Normalized from `date_requested` when present. */
  date_added?: string;
  date_requested?: string | null;
  date_resolved?: string | null;
  user_summary?: UserSummary | null;
  store_summary?: StoreSummary | null;
  [key: string]: unknown;
}

export interface PaymentMethod {
  bpmid: number;
  uuid?: string;
  uid?: number | null;
  oid?: number | null;
  /** Normalized from `card_type` / `label` when present. */
  brand?: string | null;
  card_type?: string | null;
  label?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
  channel?: string | null;
  provider?: string | null;
  is_default?: number | boolean;
  deleted?: number | boolean;
  date_added?: string;
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
  [key: string]: unknown;
}

export interface PaymentsListResponse {
  payments: Payment[];
  pagination: Pagination;
}

export interface InvoicesListResponse {
  invoices: Invoice[];
  pagination: Pagination;
}

export interface TransactionsListResponse {
  transactions: LedgerTransaction[];
  pagination: Pagination;
}

export interface SubscriptionsListResponse {
  subscriptions: Subscription[];
  pagination: Pagination;
}

export interface OrdersListResponse {
  orders: Order[];
  pagination: Pagination;
}

export interface RefundRequestsListResponse {
  refund_requests: RefundRequest[];
  pagination: Pagination;
}

export interface PaymentMethodsListResponse {
  payment_methods: PaymentMethod[];
  pagination?: Pagination;
}

export interface PaymentSourcesResponse {
  sources: PaymentSource[];
  payment_sources?: PaymentSource[];
}

export interface WalletLedgerResponse {
  transactions: WalletTransaction[];
  pagination: Pagination;
}

/** `GET /api/admin/v2/billing/overview`. Payment figures and `series` cover
 *  the last `days`; everything else is current state. */
export interface BillingOverview {
  days: number;
  since: string;
  payments: Array<{
    ccode: string;
    collected: number;
    collected_count: number;
    refunded: number;
    refunded_count: number;
    pending: number;
    pending_count: number;
    canceled_count: number;
  }>;
  series: Array<{ date: string; ccode: string; collected: number; count: number }>;
  invoices: Array<{ status: 'pending' | 'overdue'; ccode: string; count: number; amount: number }>;
  subscriptions: Record<string, { count: number; amount: number }>;
  wallets: WalletSummaryRow[];
  payouts: Partial<Record<'wallet' | 'store', { count: number; amount: number }>>;
  attention: {
    overdue_invoices: number;
    past_due_subscriptions: number;
    pending_refund_requests: number;
    open_wallet_withdrawals: number;
    open_store_payouts: number;
    pending_payments: number;
  };
}

/** One row per currency, covering every wallet (not just the page). */
export interface WalletSummaryRow {
  ccode: string;
  wallets: number;
  balance: number;
  frozen: number;
}

export interface WalletListResponse {
  wallets: Wallet[];
  pagination: Pagination;
  summary: WalletSummaryRow[];
}

export interface WalletListParams {
  frozen?: 0 | 1;
  owner?: 'user' | 'org';
  funded?: 1;
  ccode?: string;
  limit?: number;
  offset?: number;
}

export type WalletPayoutStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
export type WalletPayoutAction = 'complete' | 'fail' | 'retry';

/** An owner withdrawal (a payout with no store). */
export interface WalletPayout {
  poid: number;
  wid: number;
  /** Set on a store payout; those are managed from the store's own page. */
  storeid?: number | null;
  store_summary?: StoreSummary | null;
  uid?: number | null;
  oid?: number | null;
  amount: number;
  fee: number;
  net_amount: number;
  ccode: string;
  provider: string;
  status: WalletPayoutStatus;
  provider_reference?: string | null;
  failure_reason?: string | null;
  requested_by_uid?: number | null;
  date_requested?: string | null;
  date_processed?: string | null;
  user_summary?: UserSummary | null;
  org_summary?: OrgSummary | null;
}

export interface WalletPayoutsResponse {
  payouts: WalletPayout[];
  pagination: Pagination;
}

export interface WalletPayoutConfig {
  provider: string;
  schedule?: string;
  min_payout_amount?: number | null;
  paystack_recipient_code?: string | null;
  bank_name?: string | null;
  account_number?: string | null;
  account_name?: string | null;
  momo_msisdn?: string | null;
  momo_network?: string | null;
  auto_payout_enabled?: number | null;
}

export interface WalletAdjustPayload {
  direction: WalletDirection;
  amount: number;
  reason: string;
  uid?: number;
  oid?: number;
  wid?: number;
  ccode?: string;
}

export interface PaymentRefundPayload {
  reason?: string;
  amount?: number;
  gateway?: boolean;
}

export interface SortableListParams {
  sort_by?: string;
  order?: 'asc' | 'desc';
  q?: string;
  ffid?: string;
  amount_min?: number;
  amount_max?: number;
}

export interface PaymentListParams extends SortableListParams {
  uid?: number;
  oid?: number;
  invid?: number;
  psid?: number;
  vid?: number;
  bpmid?: number;
  status?: PaymentListStatus | '';
  ccode?: string;
  ref_id?: string;
  tester?: 0 | 1;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface InvoiceListParams extends SortableListParams {
  uid?: number;
  oid?: number;
  plid?: number;
  status?: InvoiceStatus | '';
  billing_type?: BillingType | '';
  ccode?: string;
  tester?: 0 | 1;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface TransactionListParams extends SortableListParams {
  uid?: number;
  oid?: number;
  eid?: number;
  invid?: number;
  pid?: number;
  storeid?: number;
  toid?: number;
  ttid?: number;
  fulfilled?: 0 | 1;
  ccode?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface SubscriptionListParams extends SortableListParams {
  uid?: number;
  oid?: number;
  eid?: number;
  billing_status?: SubscriptionBillingStatus | '';
  interval_type?: IntervalType | '';
  tester?: 0 | 1;
  limit?: number;
  offset?: number;
}

export interface OrderListParams extends SortableListParams {
  uid?: number;
  storeid?: number;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface RefundRequestListParams extends SortableListParams {
  status?: RefundRequestStatus | '';
  storeid?: number;
  uid?: number;
  orderid?: number;
  limit?: number;
  offset?: number;
}

export function summaryLabel(
  s?: UserSummary | OrgSummary | EntitySummary | StoreSummary | null,
): string {
  if (!s) return '—';
  if ('uid' in s) {
    return s.preferred_name || s.name || s.username || `User ${s.uid}`;
  }
  if ('oid' in s) return s.name || `Org ${s.oid}`;
  if ('eid' in s) return s.name || s.ffid || `Entity ${s.eid}`;
  if ('storeid' in s) return s.name || s.slug || `Store ${s.storeid}`;
  return '—';
}

const LIST_STATUSES: PaymentListStatus[] = ['pending', 'completed', 'canceled', 'refunded'];

/** Prefer API `status` when present; otherwise derive from flag columns. */
export function derivePaymentStatus(
  p: Pick<Payment, 'status' | 'refund' | 'fulfilled' | 'canceled'>,
): PaymentListStatus {
  if (typeof p.status === 'string' && LIST_STATUSES.includes(p.status as PaymentListStatus)) {
    return p.status as PaymentListStatus;
  }
  if (p.refund === 1 || p.refund === true) return 'refunded';
  if (p.fulfilled === 1 || p.fulfilled === true) return 'completed';
  if (p.canceled === 1 || p.canceled === true) return 'canceled';
  return 'pending';
}

export function paymentChannel(p: Pick<Payment, 'payment_method_summary' | 'source' | 'psid'>): string {
  return (
    p.payment_method_summary?.channel ||
    p.payment_method_summary?.name ||
    p.source?.channel ||
    p.source?.name ||
    (p.psid != null ? `psid ${p.psid}` : '—')
  );
}

export function paymentEntityLink(p: Pick<Payment, 'links'>): PaymentLinkEntity | null {
  const link = p.links?.find((l) => l.type === 'entity');
  return link && link.type === 'entity' ? (link as PaymentLinkEntity) : null;
}

/** Map canonical API invoice fields onto the UI aliases (`amount`, …). */
export function normalizeInvoice(raw: Invoice): Invoice {
  const amount =
    raw.amount ??
    (raw.amount_posttax != null ? Number(raw.amount_posttax) : undefined) ??
    (raw.amount_pretax != null ? Number(raw.amount_pretax) : undefined);
  return { ...raw, amount };
}

/** Map canonical API order fields onto UI aliases (`uid`, `total`, `ccode`). */
export function normalizeOrder(raw: Order): Order {
  return {
    ...raw,
    uid: raw.uid ?? raw.uid_buyer ?? null,
    total: raw.total ?? (raw.amount_total != null ? Number(raw.amount_total) : undefined),
    ccode: raw.ccode ?? raw.currency_code,
  };
}

/** Map canonical API refund-request fields onto UI aliases. */
export function normalizeRefundRequest(raw: RefundRequest): RefundRequest {
  return {
    ...raw,
    uid: raw.uid ?? raw.uid_buyer ?? null,
    notes: raw.notes ?? raw.seller_notes ?? null,
    date_added: raw.date_added ?? raw.date_requested ?? undefined,
  };
}

/** Map canonical API payment-method fields onto UI aliases (`brand`). */
export function normalizePaymentMethod(raw: PaymentMethod): PaymentMethod {
  return {
    ...raw,
    brand: raw.brand ?? raw.card_type ?? raw.label ?? null,
  };
}

export function formatMoney(amount: number | null | undefined, ccode = 'GHS'): string {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccode || 'GHS',
      minimumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${ccode || ''}`.trim();
  }
}
